/**
 * Canonical references for deterministic report tables.
 *
 * Some report exhibits are assembled from static engineering catalogues at
 * render time rather than from persisted LLM prose. Their sources therefore do
 * not naturally pass through citation finalisation. This projection appends
 * those sources to the report-wide reference register without changing any
 * existing index, and promotes matching rows to `citedInThisReport: true` so
 * they cannot disappear behind the appendix's cited-only filter.
 *
 * The function is pure, deterministic, append-only and idempotent. It is safe
 * to run at every presentation boundary, including for legacy reports.
 */
import { getSectionsForType } from '../../config/reportSections';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import { buildIndustryBaselineReferenceRows } from '../../services/financial/industryBaselines';
import {
  SOURCES,
  SOURCES_ORDERED,
  type ReferenceSource,
  type SourceId,
} from '../workloadTechnicalReference';

const WORKLOAD_REFERENCE_FIELD = 'workloadPowerFit';
const MISSING_REPORT_TIMESTAMP = '1970-01-01T00:00:00.000Z';

const LCOE_SECTION_IDS = new Set([
  'btmOverview',
  'btmTechComparison',
  'lcoeChart',
  'lcoeBreakdown',
  'financialSummary',
  'capexOpexBreakdown',
  'cashflowStream',
]);

export interface DerivedTableReferenceProjection {
  report: SiteRagReport;
  /** Report-global, one-based reference number for each workload source. */
  workloadReferenceNumbers: Partial<Record<SourceId, number>>;
  /** Report-global, one-based references used by the LCOE exhibits. */
  lcoeReferenceNumbers: number[];
}

export interface DerivedTableReferenceProjectionOptions {
  /** The caller is actively rendering the workload table outside its normal report type. */
  forceWorkload?: boolean;
  /** The caller is actively rendering an LCOE exhibit outside its normal report type. */
  forceLcoe?: boolean;
}

interface ReferenceCandidate {
  row: DataSource;
  workloadSourceId?: SourceId;
  lcoe?: boolean;
}

function referenceTimestamp(report: SiteRagReport): string {
  return report.createdAt?.trim() || report.updatedAt?.trim() || MISSING_REPORT_TIMESTAMP;
}

function normalizedSource(source: string | undefined): string {
  // `DataSource.source` is optional at runtime (legacy rows and share
  // snapshots omit it); mirror `normalizedUrl` and return an empty key so the
  // callers' `if (source && \u2026)` guards skip it instead of throwing.
  return (source ?? '')
    .replace(/[\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function normalizedUrl(url: string | undefined): string | null {
  const raw = url?.trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    parsed.hash = '';
    parsed.hostname = parsed.hostname.toLowerCase();
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return raw.replace(/\/+$/, '');
  }
}

function workloadSourceId(source: ReferenceSource): SourceId {
  const match = (Object.entries(SOURCES) as Array<[SourceId, ReferenceSource]>)
    .find(([, candidate]) => candidate === source || candidate.n === source.n);
  if (!match) throw new Error(`Unknown workload reference source number ${source.n}.`);
  return match[0];
}

function workloadReferenceCandidates(report: SiteRagReport): ReferenceCandidate[] {
  const retrievedAt = referenceTimestamp(report);
  return SOURCES_ORDERED.map((source) => ({
    workloadSourceId: workloadSourceId(source),
    row: {
      field: WORKLOAD_REFERENCE_FIELD,
      source: `${source.org} – ${source.title} (${source.year})`,
      url: source.url,
      retrievedAt,
      dataAsOf: String(source.year),
      citedInThisReport: true,
      subItems: [{ topic: 'Supports', detail: source.backs }],
    },
  }));
}

function lcoeReferenceCandidates(report: SiteRagReport): ReferenceCandidate[] {
  return buildIndustryBaselineReferenceRows(referenceTimestamp(report)).map((row) => ({
    lcoe: true,
    row: { ...row, citedInThisReport: true },
  }));
}

function enabledSectionIds(report: SiteRagReport): Set<string> {
  const ids = new Set(getSectionsForType(report.reportType).map((section) => section.sectionId));
  for (const sectionId of report.sectionOrder ?? []) ids.add(sectionId);
  for (const [sectionId, visible] of Object.entries(report.sectionVisibility ?? {})) {
    if (visible === false) ids.delete(sectionId);
  }
  return ids;
}

function relevantLcoeProviderFields(report: SiteRagReport): Set<string> {
  const fields = new Set<string>();
  const datasets = report.datasets;
  if (datasets?.solar?.ghi != null) fields.add('solar');
  if (datasets?.wind?.capacityFactor != null) fields.add('wind');
  if (datasets?.electricity?.retailRate != null) fields.add('electricity');
  // Do not attach seismic hazard references to the LCOE/technology table
  return fields;
}

function enrichedExistingReference(existing: DataSource, candidate: DataSource): DataSource {
  const next: DataSource = {
    ...existing,
    field: existing.field?.trim() ? existing.field : candidate.field,
    source: existing.source?.trim() ? existing.source : candidate.source,
    retrievedAt: existing.retrievedAt?.trim() ? existing.retrievedAt : candidate.retrievedAt,
    url: existing.url || candidate.url,
    dataAsOf: existing.dataAsOf || candidate.dataAsOf,
    subItems: existing.subItems?.length ? existing.subItems : candidate.subItems,
    citedInThisReport: true,
  };
  const unchanged = Object.keys(next).every((key) => (
    next[key as keyof DataSource] === existing[key as keyof DataSource]
  ));
  return unchanged ? existing : next;
}

/**
 * Merge every deterministic table source required by this report into its
 * canonical reference list. Existing reference order and numbers are never
 * changed; missing rows are appended in deterministic catalogue order.
 */
export function projectDerivedTableReferences(
  report: SiteRagReport,
  options: DerivedTableReferenceProjectionOptions = {},
): DerivedTableReferenceProjection {
  const sectionIds = enabledSectionIds(report);
  const carriesWorkloadTable = options.forceWorkload === true || sectionIds.has('workloadPowerFit');
  const carriesLcoeTable = options.forceLcoe === true
    || [...LCOE_SECTION_IDS].some((sectionId) => sectionIds.has(sectionId));
  const candidates: ReferenceCandidate[] = [
    ...(carriesLcoeTable ? lcoeReferenceCandidates(report) : []),
    ...(carriesWorkloadTable ? workloadReferenceCandidates(report) : []),
  ];

  if (candidates.length === 0) {
    return { report, workloadReferenceNumbers: {}, lcoeReferenceNumbers: [] };
  }

  let references = report.references ?? [];
  let changed = false;
  const ensureMutable = (): void => {
    if (!changed) {
      references = [...references];
      changed = true;
    }
  };

  const urlToIndex = new Map<string, number>();
  const sourceToIndex = new Map<string, number>();
  const indexReference = (reference: DataSource, index: number): void => {
    const url = normalizedUrl(reference.url);
    if (url && !urlToIndex.has(url)) urlToIndex.set(url, index);
    const source = normalizedSource(reference.source);
    if (source && !sourceToIndex.has(source)) sourceToIndex.set(source, index);
  };
  references.forEach(indexReference);

  const workloadReferenceNumbers: Partial<Record<SourceId, number>> = {};
  const lcoeReferenceNumbers = new Set<number>();

  for (const candidate of candidates) {
    const urlKey = normalizedUrl(candidate.row.url);
    const sourceKey = normalizedSource(candidate.row.source);
    let index = (urlKey ? urlToIndex.get(urlKey) : undefined) ?? sourceToIndex.get(sourceKey);
    if (index == null) {
      ensureMutable();
      index = references.length;
      references.push(candidate.row);
      indexReference(candidate.row, index);
    } else {
      const existing = references[index]!;
      const enriched = enrichedExistingReference(existing, candidate.row);
      if (enriched !== existing) {
        ensureMutable();
        references[index] = enriched;
        indexReference(enriched, index);
      }
    }
    const oneBased = index + 1;
    if (candidate.workloadSourceId) workloadReferenceNumbers[candidate.workloadSourceId] = oneBased;
    if (candidate.lcoe) lcoeReferenceNumbers.add(oneBased);
  }

  // Site-resource adjustments used by the LCOE exhibit already arrive as
  // provider references. Promote those existing rows rather than fabricating a
  // second citation with guessed publisher metadata.
  if (carriesLcoeTable) {
    const providerFields = relevantLcoeProviderFields(report);
    references.forEach((reference, index) => {
      if (!providerFields.has(reference.field)) return;
      if (reference.citedInThisReport !== true) {
        ensureMutable();
        references[index] = { ...reference, citedInThisReport: true };
      }
      lcoeReferenceNumbers.add(index + 1);
    });
  }

  const projectedReport = changed ? { ...report, references } : report;
  return {
    report: projectedReport,
    workloadReferenceNumbers,
    lcoeReferenceNumbers: [...lcoeReferenceNumbers].sort((a, b) => a - b),
  };
}

export function withDerivedTableReferences(report: SiteRagReport): SiteRagReport {
  return projectDerivedTableReferences(report).report;
}
